import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { DataTable } from "@/components/data-table";
import { useGlobalLoader } from "@/components/GlobalLoader";
import { useImpressao } from "@/components/ImpressaoComponent";
import { MODOS_IMPRESSAO } from "@/lib/modosImpressao";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { AdminContext } from "@/contexts/AdminContext";
import { useCategorias } from "@/contexts/CategoriasContext";
import { useContaConfig } from "@/contexts/ContaConfigContext";
import { useEpc } from "@/contexts/EpcContext";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import { useItem } from "@/contexts/ItemContext";
import { useLocalizacoes } from "@/contexts/LocalizacoesContext";
import { useSeriais } from "@/contexts/SeriaisContext";
import type { CategoriaItemResponse } from "@/types/categoria-item-response";
import type { EmpresaSSCC } from "@/types/empresa-sscc";
import type { EpcItemGerado } from "@/types/epc-item-gerado";
import type { ImpressaoItem } from "@/types/impressao-item";
import type { NewCategoriaItem } from "@/types/new-categoria-item";
import {
  brDateToISO,
  converterListaZplItemParaUrl,
  formatarData,
  verificarEpcsInvalidos,
} from "@/utils/UtilFuncoes";
import { IconClick } from "@tabler/icons-react";
import type { ColumnDef } from "@tanstack/react-table";
import { alert } from "@/contexts/AlertModalContext";
import {
  AlertTriangle,
  Eye,
  ExternalLink,
  Layers,
  MapPin,
  Package,
  Printer,
  RefreshCw,
} from "lucide-react";

const empresasParaSelecao: EmpresaSSCC[] = [
  { descricao: "iNOVACODE", valor: "7898968767", partition: 2, filter: 6 },
  { descricao: "ELF", valor: "78989964417", partition: 1, filter: 0 },
];

// Célula da coluna "Informação Individual" extraída em componente
// (evita useState dentro do render do `cell:`).
function InformacaoIndividualCell({
  row,
  setEpcs,
}: {
  row: { index: number; original: EpcItemGerado };
  setEpcs: React.Dispatch<React.SetStateAction<EpcItemGerado[]>>;
}) {
  const { t } = useTranslation();
  const { quantidade_bo, valor_bo, dt_validade_bo } = row.original;
  const [open, setOpen] = useState(false);
  const [quantidade, setQuantidade] = useState(quantidade_bo || "");
  const [valor, setValor] = useState(valor_bo ?? 0);
  const [validade, setValidade] = useState(dt_validade_bo || "");

  useEffect(() => {
    if (!open) {
      setQuantidade(quantidade_bo || "");
      setValor(valor_bo ?? 0);
      setValidade(dt_validade_bo || "");
    }
  }, [open, quantidade_bo, valor_bo, dt_validade_bo]);

  const temValores = !!quantidade || valor > 0 || !!validade;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant={temValores ? "outline" : "ghost"} size="sm">
          {temValores ? (
            <div className="flex flex-wrap gap-1">
              {quantidade && (
                <Badge variant="outline">
                  {t("sscc.quantity")}: {quantidade}
                </Badge>
              )}
              {valor > 0 && (
                <Badge variant="outline">R$ {valor.toFixed(2)}</Badge>
              )}
              {validade && (
                <Badge variant="outline">
                  {t("sscc.validity")}: {validade}
                </Badge>
              )}
            </div>
          ) : (
            <span>{t("sscc.assign")}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label className="text-xs">{t("sscc.quantity")}</Label>
            <Input
              type="number"
              value={quantidade}
              onChange={(e) => setQuantidade(e.target.value)}
              min={0}
            />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs">{t("sscc.valueBrl")}</Label>
            <Input
              type="number"
              step="0.01"
              value={valor}
              onChange={(e) => {
                const v = e.target.value;
                setValor(v === "" ? 0 : Number(v));
              }}
              min={0}
            />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs">{t("sscc.validity")}</Label>
            <Input
              type="date"
              value={validade}
              onChange={(e) => setValidade(formatarData(e.target.value, false))}
            />
          </div>
          <Button
            size="sm"
            onClick={() => {
              setEpcs((prev) =>
                prev.map((item, idx) =>
                  idx === row.index
                    ? {
                        ...item,
                        quantidade_bo: quantidade,
                        valor_bo: valor === 0 ? undefined : Number(valor),
                        dt_validade_bo: validade,
                      }
                    : item,
                ),
              );
              setOpen(false);
            }}
          >
            {t("common2.save")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Preview cell no design ANTIGO: texto "Visualizar" + IconClick (tabler)
function PreviewCellAntigo({
  item,
  coletaZplPadrao,
}: {
  item: EpcItemGerado;
  coletaZplPadrao: () => any;
}) {
  const { t } = useTranslation();
  const itemZpl = coletaZplPadrao();
  if (itemZpl && itemZpl.zpl) itemZpl.zpl = item.zpl_impressao_bo;
  if (!itemZpl) return <span>{t("sscc.noZplFound")}</span>;
  const url = converterListaZplItemParaUrl(itemZpl);
  return (
    <HoverCard>
      <HoverCardTrigger target="_blank" href={url} className="cursor-pointer">
        {t("sscc.view")} <IconClick className="inline-block text-gray-300" />
      </HoverCardTrigger>
      <HoverCardContent>
        <img src={url} alt="Preview" className="w-full h-auto" />
      </HoverCardContent>
    </HoverCard>
  );
}

// Preview cell no design NOVO: Eye + texto (lucide)
function PreviewCellNovo({
  item,
  coletaZplPadrao,
}: {
  item: EpcItemGerado;
  coletaZplPadrao: () => any;
}) {
  const { t } = useTranslation();
  const itemZpl = coletaZplPadrao();
  if (itemZpl && itemZpl.zpl) itemZpl.zpl = item.zpl_impressao_bo;
  if (!itemZpl)
    return (
      <span className="text-xs text-muted-foreground">
        {t("sscc.zplNotConfigured")}
      </span>
    );
  const url = converterListaZplItemParaUrl(itemZpl);
  return (
    <HoverCard openDelay={300} closeDelay={50}>
      <HoverCardTrigger asChild>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline cursor-pointer"
        >
          <Eye className="h-3.5 w-3.5" />
          {t("sscc.preview")}
        </a>
      </HoverCardTrigger>
      <HoverCardContent className="w-72 p-2">
        <img
          src={url}
          alt={t("sscc.previewAlt")}
          className="w-full h-auto bg-white rounded"
        />
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 flex items-center justify-center gap-1 text-xs text-primary hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          {t("sscc.openInNewTab")}
        </a>
      </HoverCardContent>
    </HoverCard>
  );
}

function ImpressaoSSCC() {
  const { t } = useTranslation();
  const { numeroDeImpressoes, contaConfig } = useContaConfig();
  const { isLoading } = useGlobalLoader();
  const { categorias, coletaCategorias, criarCategorias } = useCategorias();
  const { localizacoes, coletaLocalizacoes } = useLocalizacoes();
  const { coletaSeriais, adicionarMaisUmNosSeriais } = useSeriais();
  const { impressaoConfig, coletaZplPadrao } = useImpressaoConfig();
  const { pesquisarItensNaBase } = useItem();
  const { gerarSSCC } = useEpc();
  const { adicionarItensNaFila } = useImpressao();
  const { requireAdmin } = useContext(AdminContext)!;

  const [permitirItensRepetidos, setPermitirItensRepetidos] = useState(false);
  const [localizacaoSelecionada, setLocalizacaoSelecionada] = useState("");
  const [localizacaoSelecionada2, setLocalizacaoSelecionada2] = useState("");
  const [localizacaoSelecionada3, setLocalizacaoSelecionada3] = useState("");
  const [localizacaoSelecionada4, setLocalizacaoSelecionada4] = useState("");
  const [empresaSelecionada, setEmpresaSelecionada] = useState<
    EmpresaSSCC | undefined
  >(undefined);
  const [quantidade, setQuantidade] = useState(1);
  const [epcs, setEpcs] = useState<EpcItemGerado[]>([]);
  const [gerouListaImpressao, setGerouListaImpressao] = useState(false);
  const [epcRepetidosDialog, setEpcRepetidosDialog] = useState(false);
  const [epcRepetidosEncontrados, setEpcRepetidosEncontrados] = useState<
    Array<{ epc: string; codigo: string }>
  >([]);

  // ---- Switch de design ----
  const [novoDesign, setNovoDesign] = useState(true);

  useEffect(() => {
    const saved = localStorage.getItem("impressaoSscc.novoDesign");
    if (saved !== null) setNovoDesign(saved === "true");
  }, []);

  // ---- LocalStorage preferências (chave consistente) ----
  useEffect(() => {
    const checked = localStorage.getItem("permitirItensRepetidos");
    setPermitirItensRepetidos(checked === "true");
  }, []);

  // ---- Definir localização padrão ----
  useEffect(() => {
    if (impressaoConfig && localizacoes.length > 0) {
      const n1 = localizacoes.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao,
      );
      setLocalizacaoSelecionada(n1?._id || "");
      const n2 = n1?.children?.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao_2,
      );
      setLocalizacaoSelecionada2(n2?._id || "");
      const n3 = n2?.children?.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao_3,
      );
      setLocalizacaoSelecionada3(n3?._id || "");
      const n4 = n3?.children?.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao_4,
      );
      setLocalizacaoSelecionada4(n4?._id || "");
    }
    if (gerouListaImpressao) setGerouListaImpressao(false);
  }, [impressaoConfig, localizacoes, gerouListaImpressao]);

  // ---- Gerar EPCs SSCC ----
  useEffect(() => {
    if (empresaSelecionada) {
      const localizacoesZpl: string[] = [
        localizacoes.find((loc) => loc._id === localizacaoSelecionada)
          ?.descricao || "",
        localizacoes
          .find((n1) => n1._id === localizacaoSelecionada)
          ?.children.find((loc) => loc._id === localizacaoSelecionada2)
          ?.descricao || "",
        localizacoes
          .find((n1) => n1._id === localizacaoSelecionada)
          ?.children.find((n2) => n2._id === localizacaoSelecionada2)
          ?.children.find((loc) => loc._id === localizacaoSelecionada3)
          ?.descricao || "",
        localizacoes
          .find((n1) => n1._id === localizacaoSelecionada)
          ?.children.find((n2) => n2._id === localizacaoSelecionada2)
          ?.children.find((n3) => n3._id === localizacaoSelecionada3)
          ?.children.find((loc) => loc._id === localizacaoSelecionada4)
          ?.descricao || "",
      ];
      const novos = gerarSSCC(empresaSelecionada, localizacoesZpl, quantidade);
      setEpcs(novos);
    }
  }, [quantidade, empresaSelecionada]);

  // ---- Sincronizar ----
  const sincronizar = async () => {
    await coletaCategorias();
    await coletaSeriais();
    await coletaLocalizacoes();
    alert.success(t("sscc.syncSuccess"));
  };

  // ---- Gerar lista pra impressão ----
  const gerarListaParaImpressao = async () => {
    if (!empresaSelecionada) {
      alert.error(t("sscc.selectCompany"));
      return;
    }
    if (!impressaoConfig) {
      alert.error(t("sscc.configNotFound"));
      return;
    }
    if (!impressaoConfig?.app_id) {
      alert.error(t("sscc.informHardwareId"));
      return;
    }
    if (!localizacaoSelecionada) {
      alert.error(t("sscc.selectLocation"));
      return;
    }

    const categoriaFormatada: NewCategoriaItem = {
      descricao: empresaSelecionada.descricao,
      ean: empresaSelecionada.valor,
      id_conta: impressaoConfig.id_conta,
      inf_compl_1: "",
      inf_compl_10: "",
      inf_compl_11: "",
      inf_compl_12: "",
      inf_compl_2: "",
      inf_compl_3: "",
      inf_compl_4: "",
      inf_compl_5: "",
      inf_compl_6: "",
      inf_compl_7: "",
      inf_compl_8: "",
      inf_compl_9: "",
    };

    const categoriasECategoriasCriadas: CategoriaItemResponse[] = [
      ...categorias,
    ];

    if (!categorias.find((cat) => cat.ean === empresaSelecionada.valor)) {
      const categoriaCriada = await criarCategorias([categoriaFormatada]);
      if (categoriaCriada && categoriaCriada.length > 0) {
        categoriasECategoriasCriadas.push(categoriaCriada[0]);
      } else {
        alert.error(t("sscc.errorCreatingCategory"));
        return;
      }
    }

    const numero_de_impressoes = numeroDeImpressoes || 0;
    const listaParaImpressao: ImpressaoItem[] = epcs.map((epc, index) => ({
      _id: epc._id,
      inf_compl_1: "",
      inf_compl_2: "",
      inf_compl_3: "",
      inf_compl_4: "",
      inf_compl_5: "",
      inf_compl_6: "",
      inf_compl_7: "",
      inf_compl_8: "",
      inf_compl_9: "",
      inf_compl_10: "",
      inf_compl_11: "",
      inf_compl_12: "",
      tag: epc.epc,
      descricao: empresaSelecionada.descricao,
      codigo: empresaSelecionada.valor,
      zpl_impressao: epc.zpl_impressao_bo,
      id_categoria:
        categoriasECategoriasCriadas.find(
          (cat) => cat.ean === empresaSelecionada.valor,
        )?._id || "",
      id_conta: impressaoConfig.id_conta,
      id_usuario: impressaoConfig.id_usuario,
      id_localizacao1: localizacaoSelecionada,
      id_localizacao2: localizacaoSelecionada2,
      id_localizacao3: localizacaoSelecionada3,
      id_localizacao4: localizacaoSelecionada4,
      valor: epc.valor_bo || 0,
      quantidade: epc.quantidade_bo || "",
      dt_validade: brDateToISO(epc.dt_validade_bo) || "",
      n_impressao: (numero_de_impressoes + index + 1).toString(),
      id_externo: "",
      ultima_localizacao: {
        verificar: contaConfig?.transferencia_loc_origem || false,
        primeira_vez: true,
      },
    }));

    if (listaParaImpressao.length === 0) {
      alert.error(t("sscc.noEpcGenerated"));
      return;
    }

    if (!permitirItensRepetidos) {
      const itensNaBase = await pesquisarItensNaBase(
        listaParaImpressao.map((item) => item.tag),
      );
      if (itensNaBase.length > 0) {
        const repetidosFormatados = listaParaImpressao
          .filter((item) => itensNaBase.some((i) => i.tag === item.tag))
          .map((item) => ({ epc: item.tag, codigo: item.codigo }));
        setEpcRepetidosEncontrados(repetidosFormatados);
        setEpcRepetidosDialog(true);
        adicionarMaisUmNosSeriais(
          repetidosFormatados.map((item) => item.codigo),
        );
        return;
      }
    }

    const epcs_para_impressao = listaParaImpressao.map((item) => item.tag);
    if (verificarEpcsInvalidos(epcs_para_impressao)) {
      alert.error(t("sscc.invalidEpcs"));
      return;
    }
    adicionarItensNaFila(
      listaParaImpressao,
      undefined,
      undefined,
      MODOS_IMPRESSAO.SSCC,
    );
    setEmpresaSelecionada(undefined);
    setQuantidade(quantidade);
    setGerouListaImpressao(true);
  };

  // ---- Colunas (uma versão por design) ----
  const columnsAntigo: ColumnDef<EpcItemGerado>[] = [
    { accessorKey: "epc", header: "EPC" },
    {
      id: "info_adicional_item",
      header: t("sscc.individualInfoOptional"),
      cell: ({ row }) => (
        <InformacaoIndividualCell row={row} setEpcs={setEpcs} />
      ),
    },
    {
      id: "preview",
      header: t("sscc.preview2"),
      cell: ({ row }) => (
        <PreviewCellAntigo
          item={row.original}
          coletaZplPadrao={coletaZplPadrao}
        />
      ),
    },
  ];

  const columnsNovo: ColumnDef<EpcItemGerado>[] = [
    {
      accessorKey: "epc",
      header: "EPC",
      cell: ({ row }) => <span className="text-xs">{row.original.epc}</span>,
    },
    {
      id: "info_adicional_item",
      header: t("sscc.individualInfo"),
      cell: ({ row }) => (
        <InformacaoIndividualCell row={row} setEpcs={setEpcs} />
      ),
    },
    {
      id: "preview",
      header: t("sscc.preview2"),
      cell: ({ row }) => (
        <PreviewCellNovo
          item={row.original}
          coletaZplPadrao={coletaZplPadrao}
        />
      ),
    },
  ];

  // // Helper pra persistir o switch
  // const toggleNovoDesign = (v: boolean) => {
  //   setNovoDesign(v);
  //   localStorage.setItem("impressaoSscc.novoDesign", String(v));
  // };

  if (!impressaoConfig) return null;

  const podeImprimir =
    !!empresaSelecionada && !!localizacaoSelecionada && epcs.length > 0;

  // ===========================================
  // DESIGN ANTIGO (visual idêntico ao original)
  // ===========================================
  if (!novoDesign) {
    return (
      <div className="flex flex-col gap-2 py-4 md:gap-4 md:py-6 px-4 lg:px-6">
        <div className="flex items-center justify-between self-end gap-2">
          {/* <div className="flex items-center gap-2">
            <Label htmlFor="sscc-novo-design-antigo">Novo design</Label>
            <Switch
              id="sscc-novo-design-antigo"
              checked={novoDesign}
              onCheckedChange={toggleNovoDesign}
            />
          </div> */}
          <div className="flex items-center gap-2">
            <Label htmlFor="sscc-itens-repetidos-antigo">
              {t("sscc.allowRepeatedItems")}
            </Label>
            <Switch
              id="sscc-itens-repetidos-antigo"
              disabled={isLoading}
              checked={permitirItensRepetidos}
              onCheckedChange={(checked) => {
                requireAdmin(() => {
                  setPermitirItensRepetidos(checked);
                  localStorage.setItem(
                    "permitirItensRepetidos",
                    String(checked),
                  );
                });
              }}
            />
          </div>
          <Button variant="outline" onClick={sincronizar}>
            {t("common2.sync")}
          </Button>
          <Button onClick={gerarListaParaImpressao}>{t("common2.print")}</Button>
        </div>
        <div className="grid grid-cols-2 gap-2 ">
          <div className="grid gap-2 col-span-2 md:col-span-1">
            <Label htmlFor="impressao-sscc-codigo">{t("sscc.company")} *</Label>
            <Select
              disabled={isLoading}
              value={empresaSelecionada?.valor || ""}
              onValueChange={(value) => {
                const empresa = empresasParaSelecao.find(
                  (e) => e.valor === value,
                );
                setEmpresaSelecionada(empresa);
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t("common2.notDefined")} />
              </SelectTrigger>
              <SelectContent>
                {empresasParaSelecao.map((empresa) => (
                  <SelectItem key={empresa.valor} value={empresa.valor}>
                    {empresa.descricao}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2 col-span-2 md:col-span-1">
            <Label htmlFor="impressao-sscc-quantidade">
              {t("sscc.quantity")} *
            </Label>
            <Input
              min={1}
              defaultValue={1}
              disabled={isLoading}
              id="impressao-sscc-quantidade"
              type="number"
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                setQuantidade(!isNaN(value) && value > 0 ? value : 1);
              }}
              required
            />
          </div>
        </div>
        {empresaSelecionada && (
          <>
            <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
              {t("sscc.locations")}
            </h3>
            <div className="grid grid-cols-2 gap-2 ">
              <div className="grid gap-2">
                <Label htmlFor="impressao-sscc-localizacao-1">
                  {t("sscc.locationN1")}
                </Label>
                <Select
                  disabled={isLoading}
                  value={localizacaoSelecionada}
                  onValueChange={(value) => {
                    setLocalizacaoSelecionada(value);
                    setLocalizacaoSelecionada2("");
                    setLocalizacaoSelecionada3("");
                    setLocalizacaoSelecionada4("");
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t("common2.notDefined")} />
                  </SelectTrigger>
                  <SelectContent>
                    {localizacoes.map((localizacao) => (
                      <SelectItem key={localizacao._id} value={localizacao._id}>
                        {localizacao.descricao}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="impressao-sscc-localizacao-2">
                  {t("sscc.locationN2")}
                </Label>
                <Select
                  disabled={isLoading || !localizacaoSelecionada}
                  value={localizacaoSelecionada2}
                  onValueChange={(value) => {
                    setLocalizacaoSelecionada2(value);
                    setLocalizacaoSelecionada3("");
                    setLocalizacaoSelecionada4("");
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t("common2.notDefined")} />
                  </SelectTrigger>
                  <SelectContent>
                    {localizacoes
                      .find((n1) => n1._id === localizacaoSelecionada)
                      ?.children.map((n2) => (
                        <SelectItem key={n2._id} value={n2._id}>
                          {n2.descricao}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="impressao-sscc-localizacao-3">
                  {t("sscc.locationN3")}
                </Label>
                <Select
                  disabled={
                    isLoading ||
                    !localizacaoSelecionada ||
                    !localizacaoSelecionada2
                  }
                  value={localizacaoSelecionada3}
                  onValueChange={(value) => {
                    setLocalizacaoSelecionada3(value);
                    setLocalizacaoSelecionada4("");
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t("common2.notDefined")} />
                  </SelectTrigger>
                  <SelectContent>
                    {localizacoes
                      .find((n1) => n1._id === localizacaoSelecionada)
                      ?.children.find(
                        (n2) => n2._id === localizacaoSelecionada2,
                      )
                      ?.children.map((n3) => (
                        <SelectItem key={n3._id} value={n3._id}>
                          {n3.descricao}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="impressao-sscc-localizacao-4">
                  {t("sscc.locationN4")}
                </Label>
                <Select
                  disabled={
                    isLoading ||
                    !localizacaoSelecionada ||
                    !localizacaoSelecionada2 ||
                    !localizacaoSelecionada3
                  }
                  value={localizacaoSelecionada4}
                  onValueChange={(value) => {
                    setLocalizacaoSelecionada4(value);
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t("common2.notDefined")} />
                  </SelectTrigger>
                  <SelectContent>
                    {localizacoes
                      .find((n1) => n1._id === localizacaoSelecionada)
                      ?.children.find(
                        (n2) => n2._id === localizacaoSelecionada2,
                      )
                      ?.children.find(
                        (n3) => n3._id === localizacaoSelecionada3,
                      )
                      ?.children.map((n4) => (
                        <SelectItem key={n4._id} value={n4._id}>
                          {n4.descricao}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
              {t("sscc.item")}
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <div className="grid gap-2 col-span-2 ">
                <Label htmlFor="impressao-sscc-descricao">
                  {t("sscc.description")} *
                </Label>
                <Input
                  disabled
                  id="impressao-sscc-descricao"
                  type="text"
                  value={empresaSelecionada?.descricao || ""}
                  required
                />
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                Etiquetas
              </h3>
              <DataTable columns={columnsAntigo} data={epcs} />
            </div>
          </>
        )}

        <AlertDialog
          open={epcRepetidosDialog}
          onOpenChange={setEpcRepetidosDialog}
        >
          <AlertDialogContent className="max-h-96 overflow-y-auto">
            <AlertDialogHeader>
              <AlertDialogTitle>{t("sscc.repeatedEpcsTitle")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("sscc.repeatedEpcsDesc")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="max-h-48 overflow-y-auto">
              <div className="space-y-2">
                {epcRepetidosEncontrados.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between border-b pb-2"
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">
                        {t("sscc.epcLabel")} {item.epc}
                      </span>
                      <span className="text-xs text-gray-500">
                        {t("sscc.codeLabel")} {item.codigo}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <AlertDialogTitle className="text-base mt-4">
              {t("sscc.syncToContinue")}
            </AlertDialogTitle>
            <div className="flex justify-end gap-2">
              <AlertDialogCancel>{t("common2.cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={() => setEpcRepetidosDialog(false)}>
                {t("common2.ok")}
              </AlertDialogAction>
            </div>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  // ===========================================
  // DESIGN NOVO
  // ===========================================
  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold tracking-tight">
            {t("sscc.title")}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("sscc.subtitle")}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* <div className="flex items-center gap-2 rounded-md border bg-card px-3 h-9">
            <Switch
              id="sscc-novo-design"
              checked={novoDesign}
              onCheckedChange={toggleNovoDesign}
            />
            <Label
              htmlFor="sscc-novo-design"
              className="text-xs cursor-pointer"
            >
              Novo design
            </Label>
          </div> */}

          <div className="flex items-center gap-2 rounded-md border bg-card px-3 h-9">
            <Switch
              id="sscc-repetidos"
              disabled={isLoading}
              checked={permitirItensRepetidos}
              onCheckedChange={(checked) => {
                requireAdmin(() => {
                  setPermitirItensRepetidos(checked);
                  localStorage.setItem(
                    "permitirItensRepetidos",
                    String(checked),
                  );
                });
              }}
            />
            <Label htmlFor="sscc-repetidos" className="text-xs cursor-pointer">
              {t("sscc.allowRepeated")}
            </Label>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={sincronizar}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
            <span className="hidden sm:inline">{t("common2.sync")}</span>
          </Button>

          <Button
            size="sm"
            onClick={gerarListaParaImpressao}
            disabled={!podeImprimir || isLoading}
            title={
              !podeImprimir
                ? t("sscc.selectCompanyLocationToPrint")
                : t("sscc.printNLabels", { count: epcs.length })
            }
          >
            <Printer className="h-4 w-4" />
            <span className="hidden sm:inline">{t("common2.print")}</span>
            {podeImprimir && (
              <Badge
                variant="secondary"
                className="ml-1 h-5 px-1.5 text-[10px] bg-white/20 text-primary-foreground hover:bg-white/20"
              >
                {epcs.length}
              </Badge>
            )}
          </Button>
        </div>
      </div>

      {/* 1. Empresa + Qtd */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
              1
            </div>
            <CardTitle className="text-sm font-medium">
              {t("sscc.companyAndQuantity")}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-3">
            <div className="grid gap-2">
              <Label htmlFor="impressao-sscc-codigo-novo">
                {t("sscc.company")} <span className="text-destructive">*</span>
              </Label>
              <Select
                disabled={isLoading}
                value={empresaSelecionada?.valor || ""}
                onValueChange={(value) => {
                  const empresa = empresasParaSelecao.find(
                    (e) => e.valor === value,
                  );
                  setEmpresaSelecionada(empresa);
                }}
              >
                <SelectTrigger
                  id="impressao-sscc-codigo-novo"
                  className="w-full"
                >
                  <SelectValue placeholder={t("sscc.selectACompany")} />
                </SelectTrigger>
                <SelectContent>
                  {empresasParaSelecao.map((empresa) => (
                    <SelectItem key={empresa.valor} value={empresa.valor}>
                      <span className="font-medium">{empresa.descricao}</span>
                      <span className="text-muted-foreground ml-2 text-xs">
                        {empresa.valor}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="impressao-sscc-quantidade-novo">
                {t("sscc.quantity")} <span className="text-destructive">*</span>
              </Label>
              <Input
                min={1}
                value={quantidade}
                disabled={isLoading}
                id="impressao-sscc-quantidade-novo"
                type="number"
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setQuantidade(!isNaN(v) && v > 0 ? v : 1);
                }}
                required
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {empresaSelecionada ? (
        <>
          {/* 2. Localizações */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
                  2
                </div>
                <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  {t("sscc.locations")}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="grid gap-2">
                  <Label
                    htmlFor="sscc-loc-1"
                    className="text-xs text-muted-foreground"
                  >
                    {t("sscc.level1")} <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    disabled={isLoading}
                    value={localizacaoSelecionada}
                    onValueChange={(value) => {
                      setLocalizacaoSelecionada(value);
                      setLocalizacaoSelecionada2("");
                      setLocalizacaoSelecionada3("");
                      setLocalizacaoSelecionada4("");
                    }}
                  >
                    <SelectTrigger id="sscc-loc-1" className="w-full">
                      <SelectValue placeholder={t("common2.notDefined")} />
                    </SelectTrigger>
                    <SelectContent>
                      {localizacoes.map((l) => (
                        <SelectItem key={l._id} value={l._id}>
                          {l.descricao}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label
                    htmlFor="sscc-loc-2"
                    className="text-xs text-muted-foreground"
                  >
                    {t("sscc.level2")}
                  </Label>
                  <Select
                    disabled={isLoading || !localizacaoSelecionada}
                    value={localizacaoSelecionada2}
                    onValueChange={(v) => {
                      setLocalizacaoSelecionada2(v);
                      setLocalizacaoSelecionada3("");
                      setLocalizacaoSelecionada4("");
                    }}
                  >
                    <SelectTrigger id="sscc-loc-2" className="w-full">
                      <SelectValue placeholder={t("common2.notDefined")} />
                    </SelectTrigger>
                    <SelectContent>
                      {localizacoes
                        .find((n1) => n1._id === localizacaoSelecionada)
                        ?.children.map((n2) => (
                          <SelectItem key={n2._id} value={n2._id}>
                            {n2.descricao}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label
                    htmlFor="sscc-loc-3"
                    className="text-xs text-muted-foreground"
                  >
                    {t("sscc.level3")}
                  </Label>
                  <Select
                    disabled={
                      isLoading ||
                      !localizacaoSelecionada ||
                      !localizacaoSelecionada2
                    }
                    value={localizacaoSelecionada3}
                    onValueChange={(v) => {
                      setLocalizacaoSelecionada3(v);
                      setLocalizacaoSelecionada4("");
                    }}
                  >
                    <SelectTrigger id="sscc-loc-3" className="w-full">
                      <SelectValue placeholder={t("common2.notDefined")} />
                    </SelectTrigger>
                    <SelectContent>
                      {localizacoes
                        .find((n1) => n1._id === localizacaoSelecionada)
                        ?.children.find(
                          (n2) => n2._id === localizacaoSelecionada2,
                        )
                        ?.children.map((n3) => (
                          <SelectItem key={n3._id} value={n3._id}>
                            {n3.descricao}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label
                    htmlFor="sscc-loc-4"
                    className="text-xs text-muted-foreground"
                  >
                    {t("sscc.level4")}
                  </Label>
                  <Select
                    disabled={
                      isLoading ||
                      !localizacaoSelecionada ||
                      !localizacaoSelecionada2 ||
                      !localizacaoSelecionada3
                    }
                    value={localizacaoSelecionada4}
                    onValueChange={setLocalizacaoSelecionada4}
                  >
                    <SelectTrigger id="sscc-loc-4" className="w-full">
                      <SelectValue placeholder={t("common2.notDefined")} />
                    </SelectTrigger>
                    <SelectContent>
                      {localizacoes
                        .find((n1) => n1._id === localizacaoSelecionada)
                        ?.children.find(
                          (n2) => n2._id === localizacaoSelecionada2,
                        )
                        ?.children.find(
                          (n3) => n3._id === localizacaoSelecionada3,
                        )
                        ?.children.map((n4) => (
                          <SelectItem key={n4._id} value={n4._id}>
                            {n4.descricao}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 3. Item */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
                  3
                </div>
                <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                  <Package className="h-4 w-4 text-muted-foreground" />
                  {t("sscc.company")}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2">
                <Label
                  htmlFor="impressao-sscc-descricao-novo"
                  className="text-xs text-muted-foreground"
                >
                  {t("sscc.description")}
                </Label>
                <Input
                  disabled
                  id="impressao-sscc-descricao-novo"
                  type="text"
                  value={empresaSelecionada?.descricao || ""}
                />
              </div>
            </CardContent>
          </Card>

          {/* 4. Etiquetas */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
                    4
                  </div>
                  <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                    <Layers className="h-4 w-4 text-muted-foreground" />
                    {t("sscc.generatedLabels")}
                  </CardTitle>
                </div>
                <Badge variant="secondary" className="font-medium">
                  {t("sscc.labelsCount", { count: epcs.length })}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <DataTable columns={columnsNovo} data={epcs} />
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="py-12 flex flex-col items-center justify-center gap-3 text-muted-foreground">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
              <Package className="h-6 w-6" />
            </div>
            <div className="text-center max-w-sm">
              <p className="text-sm font-medium text-foreground">
                {t("sscc.selectCompanyToStart")}
              </p>
              <p className="text-xs">{t("sscc.ssccGeneratedByPrefix")}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AlertDialog: EPCs repetidos */}
      <AlertDialog
        open={epcRepetidosDialog}
        onOpenChange={setEpcRepetidosDialog}
      >
        <AlertDialogContent className="max-h-[90vh] overflow-hidden flex flex-col">
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-500/10">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              </div>
              <AlertDialogTitle>{t("sscc.repeatedEpcsFound")}</AlertDialogTitle>
            </div>
            <AlertDialogDescription>
              {t("sscc.repeatedEpcsNewDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="overflow-y-auto max-h-64 rounded-md border">
            <ul className="divide-y">
              {epcRepetidosEncontrados.map((item, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between px-3 py-2 gap-2"
                >
                  <span className="text-xs truncate">{item.epc}</span>
                  <Badge variant="secondary" className="">
                    {item.codigo}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>{t("common2.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => setEpcRepetidosDialog(false)}>
              <Eye className="h-4 w-4" />
              {t("common2.understood")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default ImpressaoSSCC;
